import { useCardData } from 'components/providers/CardDataProvider'
import { getArticlesToPick } from 'lib/api'
import { useCallback, useMemo, useState } from 'react'
import {
  ArticleParams,
  ARTICLE_STATES,
  Card,
  CardDataItem,
  CardmarketCondition,
  CountryEdition,
  countryEditionMap,
  GRADING_COMPANY_LABELS,
  mapBackendFinishAttributeToInternal,
  OrderArticle,
} from 'shared'
import { shallow } from 'zustand/shallow'
import { useOrderStore } from './useOrderStore'

export const useFetchPickingList = () => {
  const [
    activePickingList,
    setActivePickingList,
    setArticlesToPick,
    setArticlesPicked,
    setArticlesReadyForSorting,
  ] = useOrderStore(
    (s) => [
      s.activePickingList,
      s.setActivePickingList,
      s.setArticlesToPick,
      s.setArticlesPicked,
      s.setArticlesReadyForSorting,
    ],
    shallow
  )

  const [loading, setLoading] = useState(true)
  const cardData = useCardData()

  const cardDataMap = useMemo(() => {
    const map = new Map<number, Card>()
    for (const card of cardData) {
      map.set(card._id, card)
    }
    return map
  }, [cardData])

  const getMapOfOrderArticlesByPtOrderArticleId = (
    list: OrderArticle[]
  ): Map<number, OrderArticle[]> => {
    const map = new Map<number, OrderArticle[]>()

    for (const article of list) {
      const key = article.ptOrderArticleId ?? -1

      if (!map.has(key)) {
        map.set(key, [])
      }

      map.get(key)!.push(article)
    }

    return map
  }

  const updateAllPickedQuantity = (destinationList: OrderArticle[]) => {
    const mapOfDestinationList = getMapOfOrderArticlesByPtOrderArticleId(destinationList)

    mapOfDestinationList.forEach((orderArticles, _ptOrderArticleId) => {
      // check if at least one item has been picked in this group
      const hasPickedSome = orderArticles.some((article) => article.pickedQuantity !== 0) // questo funziona solo che ho appena fatto il fetch, altrimenti in altri contesti guardare il deltaQ

      if (hasPickedSome) {
        // Total picked per item (sum of picks per location)
        const totalPickedQuantity = orderArticles.reduce(
          (acc, article) => acc + (article.pickedQuantity ?? 0),
          0
        )

        orderArticles.forEach((a) => {
          a.pickedQuantity = totalPickedQuantity
        })
      }
    })

    // The grouping above is only there to sum the quantities, and it works on the very same
    // instances held by destinationList. Rebuilding the list out of the map would emit every
    // location of an article one after the other, undoing the order Rails sorted them in: an
    // article stored in two distant locations would drag the second row next to the first one.
    return destinationList.map((article) => new OrderArticle(article))
  }

  const callGetArticlesToPick = useCallback(
    async (sortingFields?: string[]) => {
      const isExplicitSort = sortingFields !== undefined
      if (activePickingList) {
        setLoading(true)
        setArticlesReadyForSorting(false)
        // TODO: Implement articlesToPick DTO
        try {
          const {
            articleMap,
            locationMap,
            pickings,
            sortingFields: plSortingFields,
          } = await getArticlesToPick(activePickingList.ptId, sortingFields!)

          const baseOrderArticles: OrderArticle[] = []

          const newArticlesToPick: OrderArticle[] = []
          const newArticlesPicked: OrderArticle[] = []

          pickings.forEach((picking: any) => {
            const article = articleMap[picking.ptOrderArticleId]
            if (!article) return

            const attributes: ArticleParams['attributes'] = [
              Number(article.quantity),
              article.condition as CardmarketCondition,
              Number(article.language.idLanguage),
              Number(article.mkmProductId),
              Number(article.price),
              article.comment,
              false, // isPlayset
              article.isSigned,
              article.isFirstEd,
              '', // idArticle
              null, // listedAt
              false, // isAltered
              GRADING_COMPANY_LABELS.find((entry: any) => entry.code === article.gradingCompany)
                ?.code ?? undefined, // grading provider
              article.grade ?? undefined,
              countryEditionMap.find((entry) => entry.code === article.countryEdition)?.value ??
                CountryEdition.Regular,
              mapBackendFinishAttributeToInternal(article.finishType),
              Number(article.ptArticleId),
            ]

            let card = cardDataMap.get(article.mkmProductId)

            // TODO: The product object becomes indispensable when the pt article doesn't exist in the database, because it's the only way I can create cards.
            if (!card) {
              const locNames: CardDataItem['locNames'] = {
                DE: undefined,
                IT: undefined,
                FR: undefined,
                ES: undefined,
              }

              if (article.product.locName && article.product.locName !== article.product.enName) {
                switch (article.language?.idLanguage) {
                  case 2:
                    locNames.FR = article.product.locName
                    break
                  case 3:
                    locNames.DE = article.product.locName
                    break
                  case 4:
                    locNames.ES = article.product.locName
                    break
                  case 5:
                    locNames.IT = article.product.locName
                    break
                }
              }

              const cardDataItem: CardDataItem = {
                _id: article.mkmProductId,
                rarity: 0,
                cn: article.product.nr ?? null,
                name: article.product.enName,
                set: article.product.expansionCode ?? '',
                expansion: article.product.expansion ?? '',
                onlyFoil: undefined,
                locNames,
                rarityStr: article.product.rarity ?? undefined,
                categoryName: article.product.category ?? undefined,
                expansionReleaseDate: article.product.expansionReleaseDate ?? undefined,
                colors: undefined,
                idGame: article.product.idGame ?? 99,
              }

              card = new Card({ cardDataItem })
              cardDataMap.set(article.mkmProductId ?? 0, card)
            }

            const locationInfo = picking.locationId
              ? { ...locationMap[picking.locationId], deltaQuantity: picking.deltaQuantity }
              : null

            const locations = (article.locationIds ?? [])
              .map((id: number) => locationMap[id])
              .filter(Boolean)

            const newArticle = new OrderArticle({
              attributes,
              card: card.toPlainObject(),
              articleState: picking.articleState,
              issues: picking.issues,
              ptOrderId: Number(article.ptOrderId),
              currencyCode: article.currency,
              pickingId: article.pickingId,
              pickedQuantity: picking.pickedQuantity,
              ptOrderArticleId: Number(picking.ptOrderArticleId),
              source: article.source,
              sourceArticleId: article.sourceArticleId,
              sourceOrderId: article.sourceOrderId,
              updatedAt: article.updatedAt,
              locationInfo,
              locations,
              sortIndex: picking.sortIndex,
            })

            baseOrderArticles.push(newArticle)
          })

          /**
           * Since the item I receive from the DB has the quantity picked for the single row/location as its picked quantity,
           * I need to do this whole process to update all order items with the same ID, adding all the picked quantities.
           * If the backend sends me this data already added, I will no longer need to do this process on the frontend.
           */
          const updatedPickingQty = updateAllPickedQuantity(baseOrderArticles)

          updatedPickingQty.forEach((article) => {
            if (article.issues && article.issues.length > 0) {
              article.articleState = ARTICLE_STATES.WITH_ISSUE
              newArticlesPicked.push(article)
            } else if (article.articleState === ARTICLE_STATES.PICKED) {
              newArticlesPicked.push(article)
            } else {
              newArticlesToPick.push(article)
            }
          })

          setArticlesToPick(newArticlesToPick)
          setArticlesPicked(newArticlesPicked)
          setArticlesReadyForSorting(true)
          setLoading(false)
          if (!isExplicitSort && plSortingFields !== undefined) {
            setActivePickingList({ ...activePickingList, sortingFields: plSortingFields })
          }
        } catch (e) {
          console.error(e)
          setLoading(false)
        }
      }
    },
    [activePickingList]
  )

  return { callGetArticlesToPick, loading, getMapOfOrderArticlesByPtOrderArticleId }
}
